import { useState } from "react";
import { ExternalLink, Lightbulb, MessageCircle, Play } from "lucide-react";
import { openExternalLink } from "../lib/telegram";

const VIDEO_ID = "ONLy1pPt9DQ";
const VIDEO_URL = "https://youtu.be/ONLy1pPt9DQ";
const SUPPORT_LINK = "https://t.me/hhs9h";

const STEPS: { title: string; body: string }[] = [
  { title: "ابدأ ملصقك", body: "اضغط زر (+) بالشريط السفلي، واختر «إنشاء من قالب» أو «نص بدون قالب» لخلفية شفافة." },
  { title: "اكتب اسمك وزيّنه", body: "اكتب النص، وغيّر الخط واللون والحدود والبعد الثالث من شريط الأدوات." },
  { title: "حرّكه", body: "من «الحركة» اختر حركة للنص أو أضف تأثيرات حركية، أو خلّي الاسم يُرسم بالقلم من «رسم الاسم»." },
  { title: "رتّبه على الملصق", body: "اسحب النص لتغيير مكانه، كبّره من الزوايا، وامدّه من الأطراف. وإذا غلطت اضغط تراجع." },
  { title: "احفظه بحزمتك", body: "اضغط «متابعة»، اختر الحزمة أو سوّي حزمة جديدة، ويوصلك الملصق على تيليجرام." },
];

const TIPS = [
  "الشريط الزمني تحت الملصق يخليك تحدد متى يظهر النص ومتى يختفي.",
  "تكدر تضيف لحد 6 طبقات نص وتأثيرات بنفس الملصق.",
  "مشتركو PRO وVIP يكدرون يسوون عدة أسماء دفعة وحدة من القالب.",
  "مشتركو VIP تنحفظ لهم آخر 3 مشاريع لمدة 3 أيام من قائمة إنشاء ملصق.",
];

export function GuideScreen() {
  const [playing, setPlaying] = useState(true); // يشتغل مباشرة بمجرد فتح التبويب

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 flex flex-col gap-4">
      <div className="px-1 pt-1">
        <h1 className="text-2xl font-bold tracking-tight">شرح البوت</h1>
        <p className="text-sm text-tg-hint mt-1">شوف الفيديو وبعدها ابدأ بخطوات سريعة</p>
      </div>

      {/* مشغل الفيديو */}
      <div className="rounded-3xl overflow-hidden relative" style={{ border: "1px solid rgba(255,255,255,.12)", boxShadow: "0 18px 40px -24px rgba(59,130,246,.9)" }}>
        <div className="relative w-full bg-black" style={{ aspectRatio: "16 / 9" }}>
          {playing ? (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title="شرح البوت"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          ) : (
            <button type="button" onClick={() => setPlaying(true)} aria-label="تشغيل الفيديو" className="absolute inset-0 w-full h-full">
              <img src={`https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />
              <span className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(8,12,24,.15), rgba(8,12,24,.55))" }} />
              <span className="absolute inset-0 flex items-center justify-center">
                <span
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg,#4C8DFF,#2F5BEA)", boxShadow: "0 10px 30px -6px rgba(59,130,246,.9), inset 0 1px 0 rgba(255,255,255,.3)" }}
                >
                  <Play size={26} fill="white" className="text-white translate-x-[-2px]" />
                </span>
              </span>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 px-4 py-3" style={{ background: "rgba(255,255,255,.05)" }}>
          <div className="min-w-0">
            <div className="text-sm font-bold truncate">شرح استخدام البوت</div>
            <div className="text-[11px] text-tg-hint">فيديو تعليمي كامل</div>
          </div>
          <button
            type="button"
            onClick={() => openExternalLink(VIDEO_URL)}
            className="shrink-0 h-9 px-3 rounded-xl flex items-center gap-1.5 text-xs font-semibold"
            style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)" }}
          >
            <ExternalLink size={14} />
            يوتيوب
          </button>
        </div>
      </div>

      {/* الخطوات */}
      <div className="flex flex-col gap-2.5">
        <span className="text-sm font-bold px-1">الخطوات باختصار</span>
        {STEPS.map((s, i) => (
          <div key={s.title} className="flex items-start gap-3 rounded-2xl p-3.5" style={{ background: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.1)" }}>
            <span
              className="w-8 h-8 rounded-xl flex items-center justify-center text-sm font-extrabold shrink-0"
              style={{ background: "rgba(59,130,246,.2)", border: "1px solid rgba(96,165,250,.4)", color: "#8FB4FF" }}
            >
              {i + 1}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-bold">{s.title}</div>
              <div className="text-[12.5px] text-tg-hint leading-relaxed mt-0.5">{s.body}</div>
            </div>
          </div>
        ))}
      </div>

      {/* نصائح */}
      <div className="rounded-3xl p-4 flex flex-col gap-2.5" style={{ background: "linear-gradient(160deg, rgba(59,130,246,.14), rgba(255,255,255,.03))", border: "1px solid rgba(96,165,250,.3)" }}>
        <span className="text-sm font-bold flex items-center gap-2">
          <Lightbulb size={15} style={{ color: "#8FB4FF" }} />
          نصائح سريعة
        </span>
        {TIPS.map((t) => (
          <div key={t} className="flex items-start gap-2 text-[12.5px] leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#8FB4FF" }} />
            <span>{t}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => openExternalLink(SUPPORT_LINK)}
        className="h-12 rounded-2xl flex items-center justify-center gap-2 text-sm font-bold mb-2"
        style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.14)" }}
      >
        <MessageCircle size={18} />
        عندك استفسار؟ تواصل مع الدعم
      </button>
      </div>
    </div>
  );
}
